import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IMemberSalesOrderInfoResult } from '../utils/types';
import { updateCancelSalesIndividualMaterial } from '../api';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

interface CancelOrderDialogProps {
  open: boolean;
  onClose: () => void;
  data?: IMemberSalesOrderInfoResult;
}

export default function CancelOrderDialog({
  open,
  onClose,
  data,
}: CancelOrderDialogProps) {
  const { t } = useTranslation();
  const showSnackbar = useAlertSnackbar();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { token } = useContext(AuthContext);
  const nav = useNavigate();
  const today = new Date();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!data) {
    return null;
  }

  const warningText = t(
    'orderDetailPage.cancelSalesIndividualMaterial.warningText',
  );

  const handleConfirmCancel = async () => {
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const res = await updateCancelSalesIndividualMaterial(
        data.salesIndividualId,
        data.mainSalesOrderItemId,
        4,
        token,
      );

      if (res.status === 200 || res.status === 201) {
        handleCloseDialog();
        return;
      }
      let payload: any;
      try {
        payload = await res.json();
      } catch {
        payload = {};
      }

      const detail =
        payload.detail ||
        payload.message ||
        `Request failed with status ${res.status}`;
      throw new Error(detail);
    } catch (e: any) {
      const message = e.message || 'Unknown error';
      showSnackbar(message, 'error');
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
      nav('/account/orders');
    }
  };

  const handleCloseDialog = () => {
    setErrorMessage(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main', textAlign: 'center' }}>
        {t('orderDetailPage.cancelSalesIndividualMaterial.cancelRequestTitle')}
      </DialogTitle>

      {new Date(data?.lastSalesCancellationDate) < today && (
        <Alert severity="warning" sx={{ mb: 2, cursor: 'pointer' }}>
          {t('orderDetailPage.transferTourDialog.dateWarningText')}
        </Alert>
      )}

      {!(new Date(data?.lastSalesCancellationDate) < today) && (
        <DialogContent dividers>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography>
              <strong>
                {t('orderDetailPage.cancelSalesIndividualMaterial.orderNumber')}
                :
              </strong>{' '}
              {data.orderNumber}
            </Typography>
            <Typography>
              <strong>PNR:</strong> {data.pnr}
            </Typography>
            <Typography>
              <strong>
                {t('orderDetailPage.cancelSalesIndividualMaterial.tour')}:
              </strong>{' '}
              {data.tourInfo}
            </Typography>
            <Typography>
              <strong>
                {t('orderDetailPage.cancelSalesIndividualMaterial.tourDate')}:
              </strong>{' '}
              {format(data.tourStartDate, 'dd.MM.yyyy')}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.light' }}>
            <Typography component="div" color="error.main">
              {warningText.split('\n').map((line, idx) => (
                <span key={idx}>
                  {line}
                  <br />
                </span>
              ))}
            </Typography>
          </Paper>
        </DialogContent>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ alignItems: 'center', mb: 1 }}>
          {t('orderDetailPage.cancelSalesIndividualMaterial.errorMessage')}
        </Alert>
      )}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={handleCloseDialog}>
          {t('orderDetailPage.cancelSalesIndividualMaterial.cancel')}
        </Button>
        {!data?.lastSalesCancellationDate ||
          (new Date(data.lastSalesCancellationDate) >= today && (
            <Button
              disabled={!!errorMessage || submitting}
              variant="contained"
              color="error"
              onClick={handleConfirmCancel}
            >
              {t('orderDetailPage.cancelSalesIndividualMaterial.confirmCancel')}
            </Button>
          ))}
      </DialogActions>
    </Dialog>
  );
}
